import { useRef, useState } from 'react'
import { FileUp, Link2, HardDrive, Loader2, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Input } from '@orisini/ui/components/input'
import { cn } from '@orisini/ui/lib/utils'
import { ApiError } from '@orisini/ui/lib/api'
import { mediaApi, type MediaDto } from '../api'

type Tab = 'upload' | 'url' | 'local'

const TABS: Array<{ id: Tab; label: string; icon: typeof FileUp }> = [
  { id: 'upload', label: 'Upload', icon: FileUp },
  { id: 'url', label: 'Web URL', icon: Link2 },
  { id: 'local', label: 'Local file', icon: HardDrive },
]

/**
 * Pemilih media tiga sumber.
 *
 * Ketiganya sengaja setara di UI karena ketiganya setara di backend:
 * `r2` (diunggah), `url` (gambar di web), `local` (path di komputer user).
 */
export default function MediaPicker({
  value,
  onChange,
}: {
  value: MediaDto | null
  onChange: (media: MediaDto | null) => void
}) {
  const [tab, setTab] = useState<Tab>('upload')
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState(0)
  const [urlValue, setUrlValue] = useState('')
  const [pathValue, setPathValue] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  async function run<T>(fn: () => Promise<T>) {
    setBusy(true)
    try {
      return await fn()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err))
      return null
    } finally {
      setBusy(false)
      setProgress(0)
    }
  }

  async function onFile(file: File) {
    const media = await run(() => mediaApi.upload(file, setProgress))
    if (media) {
      onChange(media)
      toast.success('Media uploaded')
    }
  }

  if (value) {
    return (
      <div className="space-y-3">
        <div className="relative overflow-hidden rounded-lg border bg-muted/30">
          {value.previewUrl && value.kind === 'image' ? (
            <img
              src={value.previewUrl}
              alt={value.filename ?? 'Media preview'}
              className="mx-auto max-h-72 w-auto object-contain"
            />
          ) : (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
              {value.kind === 'video' ? 'Video' : 'Media'} · preview not available
            </div>
          )}
          <Button
            variant="secondary"
            size="icon"
            className="absolute right-2 top-2 size-8"
            onClick={() => onChange(null)}
            title="Replace media"
          >
            <X className="size-4" />
          </Button>
        </div>

        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <dt>Source</dt>
          <dd className="text-foreground">
            {value.source === 'r2' ? 'Uploaded' : value.source === 'url' ? 'Web URL' : 'Local file'}
          </dd>
          {value.filename && (
            <>
              <dt>Name</dt>
              <dd className="truncate text-foreground">{value.filename}</dd>
            </>
          )}
          {value.localPath && (
            <>
              <dt>Path</dt>
              <dd className="truncate text-foreground">{value.localPath}</dd>
            </>
          )}
        </dl>

        {value.status === 'pending' && value.source === 'local' && (
          <p className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 text-xs text-amber-500">
            Local file not verified yet. The desktop app will check that it exists before
            posting.
          </p>
        )}
        {value.source === 'url' && (
          <p className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 text-xs text-amber-500">
            The image is fetched directly from the source URL at posting time. If the source
            disappears or is blocked before the scheduled time, the post will fail.
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-1 rounded-md bg-muted p-1">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 rounded px-3 py-1.5 text-sm transition-colors',
              tab === id
                ? 'bg-background font-medium shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="size-3.5" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'upload' && (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            const file = e.dataTransfer.files[0]
            if (file) void onFile(file)
          }}
          className="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 text-center"
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/x-m4v"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void onFile(file)
            }}
          />
          {busy ? (
            <>
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Uploading… {progress}%</p>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">Drag a file here, or</p>
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}>
                Select file
              </Button>
              <p className="text-xs text-muted-foreground">
                JPG, PNG, WEBP, GIF · MP4, MOV, M4V
              </p>
            </>
          )}
        </div>
      )}

      {tab === 'url' && (
        <div className="flex gap-2">
          <Input
            placeholder="https://example.com/image.jpg"
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
          />
          <Button
            type="button"
            disabled={busy || !urlValue.trim()}
            onClick={() =>
              void run(() => mediaApi.fromUrl(urlValue.trim())).then((r) => {
                if (r) {
                  onChange(r.media)
                  setUrlValue('')
                }
              })
            }
          >
            {busy ? <Loader2 className="animate-spin" /> : 'Use'}
          </Button>
        </div>
      )}

      {tab === 'local' && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              placeholder="C:\Users\Name\Pictures\pin.jpg"
              value={pathValue}
              onChange={(e) => setPathValue(e.target.value)}
            />
            <Button
              type="button"
              disabled={busy || !pathValue.trim()}
              onClick={() =>
                void run(() => mediaApi.local(pathValue.trim())).then((r) => {
                  if (r) {
                    onChange(r.media)
                    setPathValue('')
                  }
                })
              }
            >
              {busy ? <Loader2 className="animate-spin" /> : 'Use'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Absolute path on the computer where the Pinboss app runs. The file isn't uploaded to
            the server — the desktop app reads it directly at posting time.
          </p>
        </div>
      )}
    </div>
  )
}
